import { type ComponentType, useCallback, useEffect, useRef, useState } from "react";
import { AppFrame } from "./AppFrame";
import { DESK_SCREENS, type DeskKey } from "./deskScreens";

/**
 * "See what Abi can do" (the prototype's deskBuild): a monitor on the homepage showing the app at
 * reduced scale, switchable by a rail of tiles.
 *
 * The prototype cloned the live #app markup. Here each tab renders a component from
 * deskScreens.tsx inside an AppFrame (`<div id="app">` in a shadow root), so the app's own
 * #app-scoped stylesheet applies, the site's band rules don't, and `.appmini` gives it the app's
 * morning theme (v33).
 * The whole frame is `aria-hidden` and `inert`: it's a picture, not a second dashboard.
 *
 * It is a few hundred nodes, so it's only built once it's within 1400 px of the viewport, in an
 * idle callback, to keep it out of the scroll story's frames.
 */
const DESK: { k: DeskKey; n: string; icon: string }[] = [
  {
    k: "dashboard",
    n: "Dashboard",
    icon: '<path d="M3 10.5 12 3l9 7.5"/><path d="M5.5 9.5V21h13V9.5"/>',
  },
  { k: "cash-flow", n: "Cash flow", icon: '<path d="M3 17l5-6 4 3 5-7 4 4"/>' },
  { k: "net-worth", n: "Net worth", icon: '<path d="M4 20V9m5 11V4m5 16v-7m5 7V7"/>' },
  { k: "priorities", n: "Priorities", icon: '<path d="M4 6h16M4 12h11M4 18h7"/>' },
  {
    k: "retirement",
    n: "Retirement",
    icon: '<circle cx="12" cy="12" r="8.2"/><path d="M12 7.3V12l3.2 2"/>',
  },
  { k: "debt", n: "Debt", icon: '<path d="M4 7.5h16v10H4z"/><path d="M4 11h16"/>' },
  {
    k: "decisions",
    n: "Decisions",
    icon: '<path d="M12 4v6m0 0-4.5 4.5M12 10l4.5 4.5"/><circle cx="12" cy="3.6" r="1.4"/><circle cx="6.6" cy="16" r="1.6"/><circle cx="17.4" cy="16" r="1.6"/>',
  },
];

const APP_WIDTH = 1180;

export function DeskPreview() {
  const wrap = useRef<HTMLDivElement>(null);
  const mini = useRef<HTMLDivElement>(null);
  const main = useRef<HTMLDivElement>(null);
  const [built, setBuilt] = useState(false);
  const [idx, setIdx] = useState(0);
  const [swap, setSwap] = useState(0);

  useEffect(() => {
    const el = wrap.current;
    if (!el) return;
    el.inert = true;
    if (!("IntersectionObserver" in window)) {
      setBuilt(true);
      return;
    }
    let idle = 0;
    const io = new IntersectionObserver(
      (es) => {
        if (!es.some((e) => e.isIntersecting)) return;
        io.disconnect();
        const ric = window.requestIdleCallback;
        idle = ric
          ? ric(() => setBuilt(true), { timeout: 1200 })
          : window.setTimeout(() => setBuilt(true), 1);
      },
      { rootMargin: "1400px" },
    );
    io.observe(el);
    return () => {
      io.disconnect();
      if (window.cancelIdleCallback) window.cancelIdleCallback(idle);
      clearTimeout(idle);
    };
  }, []);

  // The screen renders at a fixed width; scale it to whatever the glass is (deskFit).
  const fit = useCallback(() => {
    const m = mini.current;
    const g = main.current;
    if (!m || !g || !g.clientWidth) return;
    m.style.transformOrigin = "top left";
    m.style.transform = `scale(${(g.clientWidth / APP_WIDTH).toFixed(4)})`;
  }, []);

  useEffect(() => {
    if (!built) return;
    fit();
    let t: ReturnType<typeof setTimeout> | undefined;
    const onResize = () => {
      clearTimeout(t);
      t = setTimeout(fit, 120);
    };
    window.addEventListener("resize", onResize, { passive: true });
    return () => {
      clearTimeout(t);
      window.removeEventListener("resize", onResize);
    };
  }, [built, fit, idx]);

  const d = DESK[idx];
  const Screen: ComponentType = DESK_SCREENS[d.k];

  return (
    <>
      <div className="deskrail" id="deskrail">
        {DESK.map((t, i) => (
          <button
            type="button"
            key={t.k}
            className={`dtile${i === idx ? " on" : ""}`}
            aria-pressed={i === idx}
            onClick={() => {
              setIdx(i);
              setSwap((s) => s + 1);
            }}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
              dangerouslySetInnerHTML={{ __html: t.icon }}
            />
            {t.n}
          </button>
        ))}
      </div>
      <div
        className="deskwrap"
        id="deskwrap"
        ref={wrap}
        aria-hidden="true"
        data-testid="desk-preview"
      >
        {built && (
          <>
            <div className="monitor">
              <div className="mbar">
                <i />
                <i />
                <i />
                <span className="url">wealthyplanet.com/app/{d.k}</span>
              </div>
              <div className={`mscreen nosb${swap ? " sw" : ""}`} key={swap}>
                <div className="mnotch" />
                <div className="mmain" ref={main}>
                  <AppFrame frameRef={mini}>
                    <Screen />
                  </AppFrame>
                </div>
              </div>
            </div>
            <div className="mstand" />
            <div className="mfoot" />
          </>
        )}
      </div>
    </>
  );
}
